import React, { useState, useEffect } from 'react'
import { TEAM_INFO } from './teams'
import HistoryView from './history/HistoryView'
import './App.css'

// The frontend is served from the same origin as the API (single app container),
// so all calls are relative. Override with VITE_API_URL only for split deployments.
const API_URL = import.meta.env.VITE_API_URL || '';

const getImageUrl = (path, fallback) => {
  if (!path) return fallback;
  if (path.startsWith('http')) return path;
  return `${API_URL}${path}`;
};

function App() {
  const [stats, setStats] = useState(null)
  const [view, setViewState] = useState(() => window.location.pathname.startsWith('/history') ? 'timeline' : 'player')
  const [selectedTeam, setSelectedTeam] = useState(null)

  // The history view lives at /history so it can be linked to and reloaded.
  const setView = (next) => {
    setViewState(next)
    const path = next === 'timeline' ? '/history' : '/'
    if (window.location.pathname !== path) window.history.pushState(null, '', path)
  }

  useEffect(() => {
    const onPop = () => setViewState(window.location.pathname.startsWith('/history') ? 'timeline' : 'player')
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/players/stats`)
      const data = await res.json()
      if (res.ok) {
        setStats(data.stats)
      }
    } catch (err) {
      console.error('Failed to fetch stats', err)
    }
  }

  useEffect(() => {
    fetchStats()
  }, [])

  return (
    <div className="app-container">
      <header className="app-header">
        <h1>NBA Name Rankings</h1>
        {stats && (
          <div className="user-controls">
            <button 
              className="btn-secondary" 
              style={{ padding: '0.2rem 0.6rem', fontSize: '0.8rem' }}
              onClick={() => {
                setView('timeline');
                setSelectedTeam(null);
              }}
            >
              Rankings history
            </button>
            <div className="stats-counter">
              <span>Ranked players: {stats.ranked_players}/{stats.total_players}</span>
            </div>
          </div>
        )}
      </header>
      
      <main className="app-main">
        <div className="view-toggle-container">
          <button 
            className="btn-secondary" 
            onClick={() => {
              if (selectedTeam) {
                setSelectedTeam(null)
              } else {
                setView(view === 'player' ? 'team' : 'player')
              }
            }}
          >
            {selectedTeam ? 'Back to Team Rankings' : (view === 'player' ? 'Switch to Team Rankings' : 'Switch to Player Rankings')}
          </button>
        </div>

        {selectedTeam ? (
          <TeamDetails team={selectedTeam} />
        ) : view === 'timeline' ? (
          <HistoryView />
        ) : view === 'player' ? (
          <PlayerRanker onPlayerRanked={fetchStats} />
        ) : (
          <TeamRankings onSelectTeam={setSelectedTeam} />
        )}
      </main>
    </div>
  )
}

function PlayerRanker({ onPlayerRanked }) {
  const [player, setPlayer] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [rankingStatus, setRankingStatus] = useState(null)

  const fetchPlayer = async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await fetch(`${API_URL}/api/players/random`)
      const data = await res.json()
      if (!res.ok) {
        if (res.status === 404) {
           setPlayer(null)
           setError("No players found!")
        } else {
           throw new Error(data.message || 'Failed to fetch player')
        }
      } else {
        setPlayer(data.player)
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPlayer()
  }, [])

  const handleRank = async (rank) => {
    try {
      setRankingStatus(`Ranking ${player.name} as ${rank}...`)
      const res = await fetch(`${API_URL}/api/rankings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          player_id: player.id,
          ranking: rank
        })
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.message || 'Failed to rank player')
      
      if (onPlayerRanked) onPlayerRanked()
      
      // Fetch next player
      await fetchPlayer()
    } catch (err) {
      setError(err.message)
    } finally {
      setRankingStatus(null)
    }
  }

  if (loading && !player) return <div className="loading-text">Loading player...</div>

  if (error && !player) return <div className="error-message">{error}</div>

  if (!player) return <div className="dashboard"><p>No player found.</p></div>

  const imageSrc = getImageUrl(player.image, 'https://via.placeholder.com/300?text=No+Image');
  const isProcessing = !!rankingStatus || loading;

  return (
    <div className="player-ranker">
      <div className={`player-card ${isProcessing ? 'processing' : ''}`}>
        <div className="player-image-container">
          <img src={imageSrc} alt={player.name} className="player-image" />
        </div>
        <h2>{player.name}</h2>
        
        {error && <div className="error-message">{error}</div>}
        
        <div className="ranking-buttons">
          <button className="rank-btn rank-s" onClick={() => handleRank('S')} disabled={isProcessing}>S</button>
          <button className="rank-btn rank-a" onClick={() => handleRank('A')} disabled={isProcessing}>A</button>
          <button className="rank-btn rank-b" onClick={() => handleRank('B')} disabled={isProcessing}>B</button>
          <button className="rank-btn rank-c" onClick={() => handleRank('C')} disabled={isProcessing}>C</button>
          <button className="rank-btn rank-f" onClick={() => handleRank('F')} disabled={isProcessing}>F</button>
        </div>
        {rankingStatus && <p className="status-text">{rankingStatus}</p>}
      </div>
    </div>
  )
}

function TeamRankings({ onSelectTeam }) {
  const [teams, setTeams] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        setLoading(true)
        const res = await fetch(`${API_URL}/api/teams/scores`)
        const data = await res.json()
        if (res.ok) {
          setTeams(data.teams)
        } else {
          throw new Error(data.message || 'Failed to fetch team scores')
        }
      } catch (err) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }
    fetchTeams()
  }, [])

  if (loading) return <div className="loading-text">Loading team rankings...</div>
  if (error) return <div className="error-message">{error}</div>

  return (
    <div className="team-rankings-container">
      <h2>Team Rankings</h2>
      <div className="teams-list">
        {teams.map((team, index) => {
          const info = TEAM_INFO[team.name] || { acronym: 'UNK', id: null }
          const logoUrl = info.id ? `/logos/${info.id}.svg` : ''
          return (
            <div key={team.id} className="team-card" onClick={() => onSelectTeam(team)}>
              <div className="team-rank">#{index + 1}</div>
              <div className="team-logo-container">
                {logoUrl ? <img src={logoUrl} alt={team.name} className="team-logo" /> : <div className="no-logo">No Logo</div>}
              </div>
              <div className="team-acronym">{info.acronym}</div>
              <div className="team-score">Score: {Number(team.score).toFixed(1)}</div>
              <div className="team-players-stats">
                {team.ranked_players}/{team.total_players} players ranked
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function TeamDetails({ team, onPlayerRanked }) {
  const [players, setPlayers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [draggingPlayerId, setDraggingPlayerId] = useState(null)

  const fetchTeamPlayers = async () => {
    try {
      setLoading(true)
      const res = await fetch(`${API_URL}/api/teams/${team.id}/players`)
      const data = await res.json()
      if (res.ok) {
        setPlayers(data.players)
      } else {
        throw new Error(data.message || 'Failed to fetch team players')
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchTeamPlayers()
  }, [team.id])

  const handleDragStart = (e, playerId) => {
    e.dataTransfer.setData('playerId', playerId)
    setDraggingPlayerId(playerId)
  }

  const handleDragEnd = () => {
    setDraggingPlayerId(null)
  }

  const handleDragOver = (e) => {
    e.preventDefault() // Necessary to allow dropping
  }

  const handleDrop = async (e, newRanking) => {
    e.preventDefault()
    setDraggingPlayerId(null)
    const playerId = e.dataTransfer.getData('playerId')
    if (!playerId) return

    // Optimistic UI update
    setPlayers(prevPlayers => prevPlayers.map(p => 
      p.id === playerId ? { ...p, ranking: newRanking } : p
    ))

    try {
      const res = await fetch(`${API_URL}/api/rankings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          player_id: playerId,
          ranking: newRanking
        })
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.message || 'Failed to update ranking')
      
      // Notify parent to update stats
      if (onPlayerRanked) onPlayerRanked()
    } catch (err) {
      console.error('Error updating ranking:', err)
      // Revert optimistic update by refetching
      fetchTeamPlayers()
    }
  }

  if (loading) return <div className="loading-text">Loading players for {team.name}...</div>
  if (error) return <div className="error-message">{error}</div>

  const tiers = ['S', 'A', 'B', 'C', 'F']
  const unranked = players.filter(p => !p.ranking)
  
  return (
    <div className="team-details-container">
      <h2>{team.name} Tier List</h2>
      <div className="tier-list">
        {tiers.map(tier => {
          const tierPlayers = players.filter(p => p.ranking === tier)
          return (
            <div 
              key={tier} 
              className="tier-row"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, tier)}
            >
              <div className={`tier-label rank-${tier.toLowerCase()}`}>{tier}</div>
              <div className="tier-players">
                {tierPlayers.length === 0 && <span className="empty-tier-text">No players in this tier</span>}
                {tierPlayers.map(p => (
                  <div 
                    key={p.id} 
                    className={`tier-player-card ${draggingPlayerId === p.id ? 'dragging' : ''}`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, p.id)}
                    onDragEnd={handleDragEnd}
                  >
                    <img src={getImageUrl(p.image, 'https://via.placeholder.com/150')} alt={p.name} draggable="false" />
                    <div className="tier-player-name">{p.name}</div>
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
      {unranked.length > 0 && (
        <div className="unranked-section">
          <h3>Unranked Players</h3>
          <div className="unranked-players">
            {unranked.map(p => (
              <div 
                key={p.id} 
                className={`tier-player-card unranked ${draggingPlayerId === p.id ? 'dragging' : ''}`}
                draggable
                onDragStart={(e) => handleDragStart(e, p.id)}
                onDragEnd={handleDragEnd}
              >
                <img src={getImageUrl(p.image, 'https://via.placeholder.com/150')} alt={p.name} draggable="false" />
                <div className="tier-player-name">{p.name}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default App
